// In-memory pub/sub. The prototype's WebSocket simulation per PRD §19.

import { seedSilent } from '@/domain/seedContext';

export type EventBusPayload = Record<string, unknown> | undefined;

export type EventBusHandler<P extends EventBusPayload = EventBusPayload> = (payload: P) => void;

type Listener = {
  type: string;
  handler: EventBusHandler;
};

const listeners = new Map<string, Set<EventBusHandler>>();
const log: Array<{ type: string; payload: EventBusPayload; timestamp: string }> = [];

export const eventBus = {
  subscribe<P extends EventBusPayload>(type: string, handler: EventBusHandler<P>) {
    if (!listeners.has(type)) listeners.set(type, new Set());
    listeners.get(type)!.add(handler as EventBusHandler);
    return () => {
      listeners.get(type)?.delete(handler as EventBusHandler);
    };
  },
  publish<P extends EventBusPayload>(type: string, payload?: P) {
    if (seedSilent()) return;
    log.push({ type, payload, timestamp: new Date().toISOString() });
    const set = listeners.get(type);
    if (!set) return;
    for (const h of set) {
      try {
        h(payload);
      } catch (err) {
        console.error('[eventBus] handler error for', type, err);
      }
    }
  },
  log() {
    return log.slice();
  },
  clear() {
    log.length = 0;
    listeners.clear();
  },
};

export function getEventLog() {
  return log.slice();
}

export type AnyListener = Listener;
